// src/pages/AnnouncementsPage.tsx
//
// ★ PHASE 6, TASK 25 — Annex A story 34: "Announcement Management".
//
// "Publication date" and "expiration date" (story 34's own words) ARE
// `effective.from`/`effective.until` — see `042_announcement.sql`'s own
// header for why there is no second pair of date fields. PSD only, both read
// and write. Export: PDF/Excel via the shared export kernel (P2C1 §12.52).
import { useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { VersionHistory } from '../components/VersionHistory'
import { FormResult } from '../components/FormResult'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import { ANNOUNCEMENT_PRIORITIES, type AnnouncementPriorityValue } from '../lib/types'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'
import { StateBadge, stateLabel } from '../components/StatusBadge'

type AnnouncementSortKey = 'announcementCode' | 'title' | 'priority' | 'effectiveFrom' | 'targetAudience'

const COLUMN_LABELS: Readonly<Record<AnnouncementSortKey, string>> = {
  announcementCode: 'Code',
  title: 'Title',
  priority: 'Priority',
  targetAudience: 'Target audience',
  effectiveFrom: 'Published from',
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  announcementCode: 'Code',
  title: 'Title',
  content: 'Content',
  note: 'Note (optional)',
} as const

/**
 * ★★ WAVE 8 (Task 3) REVIEW FINDING — the bounded fields that ONLY EXIST ON
 * AN ESTABLISH, and are therefore not part of a RETIRE's problem set.
 *
 * ⚠ Ticking the "Retire … instead of superseding it" box unmounts them and drops
 * them from the request body, but the React state lives on this page and survives
 * the unmount. Without naming them here, a value typed before the box was ticked
 * keeps `problems.any` true and **disables Save with no error visible anywhere** —
 * the Wave 5 failure `components/FieldError.tsx`'s header records verbatim. See
 * `lib/published-bounds.ts`'s `BoundsProblemOptions` for the full reasoning.
 *
 * ⚠ This list is the SUBMITTED half of `handleSubmit`'s own `!retireInstead &&`
 * spread below. If one changes, the other has to.
 */
const ESTABLISH_ONLY_FIELDS = ['title', 'content'] as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

export function AnnouncementsPage() {
  // ★★★ WAVE 12 (P4) — the version history and its bi-temporal rewind. The series
  // route has answered since Phase 6 and NOTHING called it until this wave; the
  // RTM's Annex A 26-28 row carried that as a gap, because an endpoint no client
  // calls is indistinguishable from one that does not work.
  //
  // ⚠ The fetcher RESOLVES TO NULL until a code is chosen rather than calling the
  // route with an empty key: that URL is the LIST route, which would return the
  // whole catalogue into a component that renders it as one code's history.
  const [historySubject, setHistorySubject] = useState('')
  const [historyAsRecordedAt, setHistoryAsRecordedAt] = useState('')
  const history = useApi(
    () =>
      historySubject.length === 0
        ? Promise.resolve(null)
        : api.announcementSeries(historySubject, historyAsRecordedAt),
    [historySubject, historyAsRecordedAt],
  )
  const { sort, onSort, interacted } = useSort<AnnouncementSortKey>()
  const announcements = useApi(() => api.announcements(sortParams(sort)), [sort.column, sort.dir])

  const [announcementCode, setAnnouncementCode] = useState('')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [priority, setPriority] = useState<AnnouncementPriorityValue>('MEDIUM')
  const [targetAudience, setTargetAudience] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  const rows = announcements.data?.announcements ?? []
  const total = announcements.data?.total ?? 0

  /*
    ⚠ The bounds come from the PAYLOAD (`validation`), never from this file, and
    they are checked against the value that will actually be SENT rather than the
    keystrokes in the box — see `lib/published-bounds.ts` for why both of those
    matter. `undefined` while the list is loading or refused, in which case the
    fields show nothing beyond `required` and the route refuses on its own.
  */
  const validation = announcements.data?.validation
  const problems = boundsProblems(
    validation,
    {
      announcementCode: announcementCode.trim().toUpperCase(),
      title: title.trim(),
      content: content.trim(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
    // ⚠ A RETIRE sends none of the establish-only fields, so none of them can be
    // in breach — see ESTABLISH_ONLY_FIELDS above for the disabled-with-no-message
    // defect this closes.
    { notSubmitted: retireInstead ? ESTABLISH_ONLY_FIELDS : [] },
  )

  const canSubmit =
    announcementCode.trim().length > 0 &&
    effectiveFrom.length > 0 &&
    !problems.any &&
    (retireInstead || (title.trim().length > 0 && content.trim().length > 0))

  /**
   * ★★ WAVE 26 — Publish FREEZES a sentence and asks; the write happens only from
   * the confirmation (`ConfirmWrite`'s header says why every irreversible write
   * does this now). Composed once, from the fields as they were when Publish was
   * pressed — never re-derived from a form the officer may since have edited.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const upper = announcementCode.trim().toUpperCase()
    const audience = targetAudience.trim()
    setConfirming(
      retireInstead
        ? `Expire announcement ${upper} from ${effectiveFrom}. No successor opens, so from that day it is no longer current and nobody sees it. There is no undo. A mistake is corrected by another version.`
        : `Publish announcement ${upper} from ${effectiveFrom}: "${title.trim()}", priority ${stateLabel(priority).toLowerCase()}${audience.length > 0 ? `, for ${audience}` : ''}. Any version current on that day is superseded by this one. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeAnnouncement(announcementCode.trim().toUpperCase(), {
        effectiveFrom,
        establish: !retireInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
        ...(!retireInstead && {
          title: title.trim(),
          content: content.trim(),
          priority,
          targetAudience: targetAudience.trim().length > 0 ? targetAudience.trim() : null,
        }),
      })) as { outcome: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `${announcementCode.trim().toUpperCase()} expires from ${effectiveFrom}.`
            : `${announcementCode.trim().toUpperCase()} published from ${effectiveFrom}.`,
      })
      setAnnouncementCode('')
      setTitle('')
      setContent('')
      setPriority('MEDIUM')
      setTargetAudience('')
      setEffectiveFrom('')
      setNote('')
      announcements.reload()
    } catch (error) {
      setForm({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this announcement.' })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Announcements</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            System announcements. "Publication date" and "expiration date" are the
            effective-from/effective-until window below: superseding an announcement is how its content
            changes; expiring it is how it stops being current. PSD-only.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.announcementExportUrl(format)
            }}
            email={<EmailReportButton path="/api/administration/announcements/export" />}
          />
        </div>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={announcements.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {announcements.error !== null ? (
        <ErrorCard title="Could not load announcements" message={announcements.error} onRetry={announcements.reload} />
      ) : announcements.initialLoading ? (
        <LoadingCard label="Loading announcements" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No announcements in scope"
          message="Either nothing is currently published, or your active identity is not PSD: this catalogue is administered by PSD only."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={announcements.loading}>
            <table className="pt-table">
              <caption className="sr-only">Announcements currently published, with code, title, priority, target audience and publication date</caption>
              <thead>
                <tr>
                  <SortableTh label="Code" sortKey="announcementCode" sort={sort} onSort={onSort} />
                  <SortableTh label="Title" sortKey="title" sort={sort} onSort={onSort} />
                  <SortableTh label="Priority" sortKey="priority" sort={sort} onSort={onSort} />
                  <SortableTh label="Target audience" sortKey="targetAudience" sort={sort} onSort={onSort} />
                  <SortableTh label="Published from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-semibold pt-mono">{row.announcementCode}</td>
                    <td>{row.title}</td>
                    <td>
                      <StateBadge kind="priority" value={row.priority} />
                    </td>
                    <td className="text-sm">{row.targetAudience ?? 'None'}</td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {formatIsoDate(row.effective.from)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} currently published announcement{total === 1 ? '' : 's'}.{' '}
            {sort.column === null ? 'The server default order.' : `Sorted by ${COLUMN_LABELS[sort.column]} (${sort.dir}).`}
          </p>
        </div>
      )}

      {/* ★★★ WAVE 12 (P4) — the version history, below the catalogue it belongs to.
          The code picker lives in the component rather than as a column on this
          table: twelve tables of differing shapes meant twelve thead/tbody edits,
          twelve chances to break a working screen for one control. */}
      <VersionHistory
        subject={historySubject}
        subjectOptions={rows.map((row) => row.announcementCode)}
        subjectHint="The announcement codes listed above. Sort or filter that table to reach others."
        onSubjectChange={(next) => {
          // ⚠ Changing code RESETS the rewind: an instant chosen while reading one
          // code is not a statement about another, and carrying it over would
          // silently reconstruct the new one at a forgotten instant.
          setHistoryAsRecordedAt('')
          setHistorySubject(next)
        }}
        entries={history.data?.entries ?? []}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        asRecordedAt={historyAsRecordedAt}
        onAsRecordedAtChange={setHistoryAsRecordedAt}
        columns={[
            { header: 'Title', cell: (entry) => entry.title },
            { header: 'Priority', cell: (entry) => <StateBadge kind="priority" value={entry.priority} /> },
        ]}
      />
      <div className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Publish, update or expire an announcement</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)]">
            Superseding is the only way an announcement's content changes: there is no edit-in-place.
          </p>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <BoundedField
            label={BOUNDED_FIELDS.announcementCode}
            example="e.g. WELCOME."
            value={announcementCode}
            onValueChange={setAnnouncementCode}
            bounds={validation?.announcementCode}
            problem={problems.of.announcementCode}
            required
          />
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold">
              Publish from <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={retireInstead} onChange={(e) => setRetireInstead(e.target.checked)} />
            Expire this announcement instead of superseding it (no successor opens)
          </label>
          {!retireInstead && (
            <>
              <BoundedField
                label={BOUNDED_FIELDS.title}
                value={title}
                onValueChange={setTitle}
                bounds={validation?.title}
                problem={problems.of.title}
                required
              />
              <SgdsSelect label="Priority" value={priority} onSgdsChange={(e) => setPriority(targetValue(e) as AnnouncementPriorityValue)}>
                {ANNOUNCEMENT_PRIORITIES.map((p) => (
                  <SgdsSelectOption key={p} value={p}>
                    {stateLabel(p)}
                  </SgdsSelectOption>
                ))}
              </SgdsSelect>
              <SgdsInput placeholder="" label="Target audience (optional)" value={targetAudience} onSgdsInput={(e) => setTargetAudience(targetValue(e))} />
              <BoundedField
                className="sm:col-span-2"
                label={BOUNDED_FIELDS.content}
                value={content}
                onValueChange={setContent}
                bounds={validation?.content}
                problem={problems.of.content}
                multiline
                rows={6}
                required
              />
            </>
          )}
          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
              {retireInstead ? 'Expire' : 'Publish'}
            </SgdsButton>
          </div>
        </form>
      </div>
    </div>
  )
}
